import classNames from 'classnames'
import moment from 'moment'
import { type ChangeEvent, type Dispatch, type SetStateAction } from 'react'

import { BlurApp, Button, CenteredBlock, Icon } from '@/shared'
import { type IFeedbackMode, type IEditFeedback, type IUpdateFeedback } from '@App/types/doctorsType'
import { EIcons } from '@App/types/iconType'

import { EditFeedback } from './EditFeedback/EditFeedback'
import style from './FeedbacksList.module.scss'

interface IActionMapperItem {
    title: string
    callback: (feedbackId: string) => void
    className: string
    disabled: boolean
}

interface IProps {
    initialState: IEditFeedback[]
    state: IEditFeedback[]
    setState: Dispatch<SetStateAction<IEditFeedback[]>>
    setMode: Dispatch<SetStateAction<IFeedbackMode[]>>
    filterCode: boolean
    onUpdateFeedback: (dto: IUpdateFeedback, onSuccess?: () => void) => void
    onRemoveFeedback: (feedbackId: string) => void
    isFetchingFeedbacks: boolean
}

export const FeedbacksList = (props: IProps) => {
    const {
        initialState,
        state,
        setState,
        filterCode,
        onUpdateFeedback,
        onRemoveFeedback,
        setMode,
        isFetchingFeedbacks
    } = props
    const onHandleUpdateFeedback = (feedbackId: string) => {
        const dto = state.find((item) => item.id === feedbackId)
        if (dto) {
            const { isEdit, ...rest } = dto
            const onSuccess = () => {
                setMode((prev) => prev.map((mode) => ({ ...mode, isChecked: !mode.isChecked })))
            }
            onUpdateFeedback({ ...rest, isPublished: !rest.isPublished }, onSuccess)
        }
    }
    const ACTIONS_MAPPER: Record<number, IActionMapperItem[]> = {
        1: [
            {
                title: 'Снять публикацию',
                callback: onHandleUpdateFeedback,
                className: 'blue',
                disabled: isFetchingFeedbacks
            }
        ],
        0: [
            {
                title: 'Опубликовать',
                callback: onHandleUpdateFeedback,
                className: 'blue',
                disabled: isFetchingFeedbacks
            },
            { title: 'Удалить', callback: onRemoveFeedback, className: 'red', disabled: isFetchingFeedbacks }
        ]
    }

    const onHandleIsEdit = (state: IEditFeedback[], id: string, isEdit: boolean) =>
        state.map((feedback) => (feedback.id === id ? { ...feedback, isEdit } : { ...feedback, isEdit: !isEdit }))

    const onHandleContent = (state: IEditFeedback[], id: string, content: string) =>
        state.map((feedback) => (feedback.id === id ? { ...feedback, content } : feedback))

    return (
        <div className={style.container}>
            {state.map((feedback) => {
                const { id, author, createdAt, content, isEdit, isPublished } = feedback
                const actions = ACTIONS_MAPPER[Number(filterCode)]
                const onEditFeedback = () => {
                    setState((prev) => onHandleIsEdit(prev, id, !isEdit))
                    isEdit && setState(initialState)
                }

                const onSaveUpdatedFeedback = () => onUpdateFeedback({ id, content, isPublished })

                const onHandleChange = (e: ChangeEvent<HTMLTextAreaElement>) =>
                    setState((prev) => onHandleContent(prev, id, e.target.value))
                return (
                    <div key={id} className={classNames(style.feedbackWrapper, isEdit && style.bluredFeedbackWrapper)}>
                        <BlurApp show={isEdit} />
                        <div className={style.name}>
                            <span>{author}</span>
                        </div>
                        <div className={style.date}>
                            <span>{moment(createdAt).locale('ru').utc().format('DD MMMM YYYY')}</span>
                        </div>
                        <EditFeedback
                            content={content}
                            isEdit={isEdit}
                            onChange={onHandleChange}
                            onEnter={onEditFeedback}
                        />
                        {actions && !isEdit ? (
                            <div className={style.actions}>
                                {actions.map((action) => {
                                    const { title, callback, disabled, className } = action
                                    const cn = classNames(style.btn, style[className])
                                    return (
                                        <Button
                                            key={title}
                                            className={cn}
                                            onClick={() => callback(id)}
                                            disabled={disabled}
                                        >
                                            {action.title}
                                        </Button>
                                    )
                                })}
                            </div>
                        ) : (
                            <div className={style.actions}>
                                <Button
                                    className={classNames(style.btn, style.blue)}
                                    onClick={onSaveUpdatedFeedback}
                                    disabled={!content || isFetchingFeedbacks}
                                >
                                    Сохранить
                                </Button>
                            </div>
                        )}
                        <CenteredBlock className={style.editBtn} onClick={onEditFeedback}>
                            <Icon iconName={isEdit ? EIcons.CLOS : EIcons.PEN} color='#007DEE' />
                        </CenteredBlock>
                    </div>
                )
            })}
        </div>
    )
}
